export type SettingValueType = 'string' | 'number' | 'boolean' | 'enum' | 'object'

export type SettingValue<T extends SettingValueType> = T extends 'string'
	? string
	: T extends 'number'
		? number
		: T extends 'boolean'
			? boolean
			: T extends 'enum'
				? string
				: T extends 'object'
					? Record<string, unknown>
					: never

export interface SettingDefinition<T extends SettingValueType = SettingValueType> {
	/** Dot-notated key, e.g. "editor.fontSize" or "view.showNodeIds" */
	key: string

	/** The value type */
	type: T

	/** Default value — always present, always valid */
	defaultValue: SettingValue<T>

	/** Translation key for the title shown in the settings UI; empty for untitled controls. */
	title: string

	/** Translation key for the description / help text. */
	description?: string

	/** Translation key identifying the tab. Defaults to "ViewSettings.tabs.general". */
	tab?: string

	/** Translation keys forming the category path for nested grouping within the tab. */
	category: string[]

	/** For type: 'enum' — the allowed values */
	enumValues?: string[]

	/** For type: 'enum' — translation keys for each enum value's label. */
	enumLabels?: string[]

	/** For type: 'number' — min/max/step constraints */
	minimum?: number
	maximum?: number
	step?: number

	/** Validation function. Returns null if valid, error string if invalid. */
	validate?: (value: SettingValue<T>) => string | null

	/** Tags for search/filtering in the settings UI */
	tags?: string[]

	/** Display order within its category (lower = higher priority) */
	order?: number

	/** If true, the setting is registered but hidden from the UI */
	hidden?: boolean

	/** For type: 'boolean' — keys of settings that are switched on and locked while this one is on */
	enables?: readonly string[]
}

export interface SettingsContribution {
	/** Namespace prefix, e.g. "smb", "chat". Used for grouping in the UI. */
	namespace: string

	/** Translation key for this group of settings. */
	displayName: string

	/** Category names that should show a "Toggle all" checkbox */
	toggleAllCategories?: string[]

	/** The setting definitions this contribution provides */
	definitions: SettingDefinition[]
}

export type StoredSettings = Record<string, unknown>

export const SETTINGS_VERSION = 1 as const

export interface PersistedSettingsEnvelope {
	/** Schema version for future migrations */
	version: typeof SETTINGS_VERSION

	/** ISO timestamp of last modification */
	lastModified: string

	/** The active preset id, or null if custom / no preset */
	activePreset: string | null

	/** Sparse map of user-overridden values */
	values: StoredSettings
}

export interface SettingsPreset {
	/** Unique identifier, e.g. "compact", "debug" */
	id: string

	/** Human-readable name */
	name: string

	/** Description shown in preset picker */
	description?: string

	/** Sparse overrides — only keys that differ from defaults */
	values: StoredSettings
}
